import compact from 'lodash/compact';
import head from 'lodash/head';
import isNil from 'lodash/isNil';
import sortBy from 'lodash/sortBy';

import {
  BankAccount,
  BankAccountType,
  CounterpartyIdentityKind,
  Transaction,
  TransactionClassificationReason,
  TransactionFlowType,
  TransactionLink,
  TransactionLinkType,
  TransactionStatus,
} from '../types/model-app';

import { BankTransactionCodes, BankTransactionNature, getBankTransactionNature } from './bankTransactionCodeUtils';
import { THIRD_PARTY_BANK_ACCOUNT_TYPES } from './bankutils';
import {
  CounterpartyIdentityLookup,
  findCounterpartyIdentity,
  getPointIdentityIdsOfTransaction,
} from './counterpartyIdentity';
import { getIdentityOfBankAccount } from './counterpartyIdentityPreCreation';
import { CounterpartyRoleTargets, getFlowTypeOfRoleDirection, hasLeaseAndCounterpartyRoles } from './counterpartyRoles';

export const CERTAIN_CLASSIFICATION_CONFIDENCE = 100;
export const STRONG_CLASSIFICATION_CONFIDENCE = 90;
export const LIKELY_CLASSIFICATION_CONFIDENCE = 80;
export const SUGGESTED_CLASSIFICATION_CONFIDENCE = 60;
export const NO_CLASSIFICATION_CONFIDENCE = 0;

export interface FlowClassification {
  flowType: TransactionFlowType;
  confidence: number;
  reason: TransactionClassificationReason;
}

export interface FlowClassificationInput extends BankTransactionCodes {
  amount: number;
  ownAccountType?: BankAccountType | null;
  counterpartyKind?: CounterpartyIdentityKind | null;
  counterpartyTargets?: CounterpartyRoleTargets;
  isEnteredOwnerAccount?: boolean;
}

export type FlowClassificationRule = (input: FlowClassificationInput) => FlowClassification | undefined;

export const UNKNOWN_FLOW_CLASSIFICATION: FlowClassification = {
  flowType: TransactionFlowType.UNKNOWN,
  confidence: NO_CLASSIFICATION_CONFIDENCE,
  reason: TransactionClassificationReason.NO_SIGNAL,
};

const isFeeWithdrawalToAgency = ({ amount, ownAccountType }: FlowClassificationInput): boolean =>
  amount < 0 && !isNil(ownAccountType) && THIRD_PARTY_BANK_ACCOUNT_TYPES.includes(ownAccountType);

const hasSeveralRoles = ({ counterpartyTargets }: FlowClassificationInput): boolean =>
  !isNil(counterpartyTargets) && hasLeaseAndCounterpartyRoles(counterpartyTargets);

const classifyOnOwnAccounts: FlowClassificationRule = (input) => {
  if (input.isEnteredOwnerAccount && !hasSeveralRoles(input)) {
    return {
      flowType: TransactionFlowType.OWNER,
      confidence: CERTAIN_CLASSIFICATION_CONFIDENCE,
      reason: TransactionClassificationReason.OWNER_ACCOUNT,
    };
  }
  switch (input.counterpartyKind) {
    case CounterpartyIdentityKind.INTERNAL_ACCOUNT:
      return {
        flowType: TransactionFlowType.INTERNAL_TRANSFER,
        confidence: CERTAIN_CLASSIFICATION_CONFIDENCE,
        reason: TransactionClassificationReason.INTERNAL_ACCOUNT,
      };
    case CounterpartyIdentityKind.AGENCY_ACCOUNT:
      return isFeeWithdrawalToAgency(input)
        ? {
            flowType: TransactionFlowType.FEES,
            confidence: SUGGESTED_CLASSIFICATION_CONFIDENCE,
            reason: TransactionClassificationReason.AGENCY_ACCOUNT,
          }
        : {
            flowType: TransactionFlowType.INTERNAL_TRANSFER,
            confidence: CERTAIN_CLASSIFICATION_CONFIDENCE,
            reason: TransactionClassificationReason.AGENCY_ACCOUNT,
          };
    default:
      return;
  }
};

const CERTAIN_NATURE_CLASSIFICATIONS: Partial<Record<BankTransactionNature, FlowClassification>> = {
  [BankTransactionNature.FEES]: {
    flowType: TransactionFlowType.BANK_FEE,
    confidence: STRONG_CLASSIFICATION_CONFIDENCE,
    reason: TransactionClassificationReason.BANK_FEE_CODE,
  },
  [BankTransactionNature.INTEREST]: {
    flowType: TransactionFlowType.BANK_FEE,
    confidence: STRONG_CLASSIFICATION_CONFIDENCE,
    reason: TransactionClassificationReason.INTEREST_CODE,
  },
  [BankTransactionNature.LOAN]: {
    flowType: TransactionFlowType.CREDIT,
    confidence: STRONG_CLASSIFICATION_CONFIDENCE,
    reason: TransactionClassificationReason.LOAN_CODE,
  },
};

const classifyOnBankCode: FlowClassificationRule = (input) =>
  CERTAIN_NATURE_CLASSIFICATIONS[getBankTransactionNature(input)];

const IDENTITY_KIND_FLOW_TYPES: Partial<Record<CounterpartyIdentityKind, FlowClassification>> = {
  [CounterpartyIdentityKind.BANK_FEES]: {
    flowType: TransactionFlowType.BANK_FEE,
    confidence: STRONG_CLASSIFICATION_CONFIDENCE,
    reason: TransactionClassificationReason.COUNTERPARTY_IDENTITY,
  },
  [CounterpartyIdentityKind.CREDIT_CONTRACT]: {
    flowType: TransactionFlowType.CREDIT,
    confidence: STRONG_CLASSIFICATION_CONFIDENCE,
    reason: TransactionClassificationReason.COUNTERPARTY_IDENTITY,
  },
  [CounterpartyIdentityKind.SYNDIC_ACP]: {
    flowType: TransactionFlowType.CALL_FOR_FUNDS,
    confidence: LIKELY_CLASSIFICATION_CONFIDENCE,
    reason: TransactionClassificationReason.COUNTERPARTY_IDENTITY,
  },
  [CounterpartyIdentityKind.SUPPLIER]: {
    flowType: TransactionFlowType.SUPPLIER,
    confidence: LIKELY_CLASSIFICATION_CONFIDENCE,
    reason: TransactionClassificationReason.COUNTERPARTY_IDENTITY,
  },
  [CounterpartyIdentityKind.OWNER_ACCOUNT]: {
    flowType: TransactionFlowType.OWNER,
    confidence: LIKELY_CLASSIFICATION_CONFIDENCE,
    reason: TransactionClassificationReason.COUNTERPARTY_IDENTITY,
  },
  [CounterpartyIdentityKind.TENANT_PAYER]: {
    flowType: TransactionFlowType.RENT,
    confidence: LIKELY_CLASSIFICATION_CONFIDENCE,
    reason: TransactionClassificationReason.COUNTERPARTY_IDENTITY,
  },
};

const classifyOnCounterpartyRoles: FlowClassificationRule = ({ amount, counterpartyTargets }) => {
  if (isNil(counterpartyTargets) || !hasLeaseAndCounterpartyRoles(counterpartyTargets)) {
    return;
  }
  return {
    flowType: getFlowTypeOfRoleDirection(amount, counterpartyTargets),
    confidence: SUGGESTED_CLASSIFICATION_CONFIDENCE,
    reason: TransactionClassificationReason.COUNTERPARTY_ROLE_DIRECTION,
  };
};

const classifyOnIdentityKind: FlowClassificationRule = ({ counterpartyKind }) =>
  isNil(counterpartyKind) ? undefined : IDENTITY_KIND_FLOW_TYPES[counterpartyKind];

const OUTGOING_CARD_OR_CASH_NATURES = [
  BankTransactionNature.CARD_PAYMENT,
  BankTransactionNature.CARD_SETTLEMENT,
  BankTransactionNature.CASH_WITHDRAWAL,
];

const RETURN_CLASSIFICATION: FlowClassification = {
  ...UNKNOWN_FLOW_CLASSIFICATION,
  reason: TransactionClassificationReason.RETURN_CODE,
};

const classifyOnReturnCode: FlowClassificationRule = (input) =>
  getBankTransactionNature(input) === BankTransactionNature.RETURN ? RETURN_CLASSIFICATION : undefined;

const classifyOnWeakBankCode: FlowClassificationRule = (input) => {
  const nature = getBankTransactionNature(input);
  if (nature === BankTransactionNature.TAX) {
    return {
      flowType: TransactionFlowType.MISC_OPERATION,
      confidence: SUGGESTED_CLASSIFICATION_CONFIDENCE,
      reason: TransactionClassificationReason.TAX_CODE,
    };
  }
  if (input.amount < 0 && OUTGOING_CARD_OR_CASH_NATURES.includes(nature)) {
    return {
      flowType: TransactionFlowType.MISC_OPERATION,
      confidence: SUGGESTED_CLASSIFICATION_CONFIDENCE,
      reason: TransactionClassificationReason.CARD_OR_CASH_CODE,
    };
  }
  return;
};

export const DEFAULT_FLOW_CLASSIFICATION_RULES: FlowClassificationRule[] = [
  classifyOnOwnAccounts,
  classifyOnBankCode,
  classifyOnReturnCode,
  classifyOnCounterpartyRoles,
  classifyOnIdentityKind,
  classifyOnWeakBankCode,
];

export const classifyTransactionFlow = (
  input: FlowClassificationInput,
  rules: FlowClassificationRule[] = DEFAULT_FLOW_CLASSIFICATION_RULES
): FlowClassification =>
  rules.reduce<FlowClassification | undefined>((found, rule) => found ?? rule(input), undefined) ??
  UNKNOWN_FLOW_CLASSIFICATION;

export const getEnteredOwnerAccountIdentityIds = (
  bankAccounts: Pick<
    BankAccount,
    'number' | 'type' | 'contactId' | 'clientId' | 'disabled' | 'holderName' | '_deleted'
  >[]
): Set<string> =>
  new Set(
    bankAccounts.reduce<string[]>((acc, bankAccount) => {
      const identity = getIdentityOfBankAccount(bankAccount);
      if (identity?.kind === CounterpartyIdentityKind.OWNER_ACCOUNT) {
        acc.push(identity.id);
      }
      return acc;
    }, [])
  );

export const isEnteredOwnerAccountMovement = (
  clientId: string,
  transaction: Pick<Transaction, 'counterpartAccountNumber' | 'mandateId' | 'creditorId'>,
  enteredOwnerAccountIdentityIds: ReadonlySet<string>
): boolean =>
  getPointIdentityIdsOfTransaction(clientId, transaction).some((identityId) =>
    enteredOwnerAccountIdentityIds.has(identityId)
  );

export const classifyBankMovement = (
  transaction: Pick<
    Transaction,
    | 'amount'
    | 'bankTransactionCode'
    | 'proprietaryBankTransactionCode'
    | 'counterpartAccountNumber'
    | 'mandateId'
    | 'creditorId'
    | 'remittanceInformation'
    | 'counterpartName'
  >,
  identityLookup: CounterpartyIdentityLookup,
  clientId: string,
  enteredOwnerAccountIdentityIds: ReadonlySet<string>,
  ownAccountType?: BankAccountType | null,
  counterpartyTargets?: CounterpartyRoleTargets
): FlowClassification =>
  classifyTransactionFlow({
    amount: transaction.amount,
    bankTransactionCode: transaction.bankTransactionCode,
    proprietaryBankTransactionCode: transaction.proprietaryBankTransactionCode,
    ownAccountType,
    counterpartyKind: findCounterpartyIdentity(identityLookup, clientId, transaction)?.kind,
    counterpartyTargets,
    isEnteredOwnerAccount: isEnteredOwnerAccountMovement(clientId, transaction, enteredOwnerAccountIdentityIds),
  });

const FLOW_TYPES_OUTSIDE_LEASE_MATCHING = [
  TransactionFlowType.BANK_FEE,
  TransactionFlowType.CREDIT,
  TransactionFlowType.INTERNAL_TRANSFER,
  TransactionFlowType.OWNER,
];

export const isOutsideLeaseMatching = ({ flowType, confidence }: FlowClassification): boolean =>
  FLOW_TYPES_OUTSIDE_LEASE_MATCHING.includes(flowType) && confidence >= STRONG_CLASSIFICATION_CONFIDENCE;

const LINK_TYPE_FLOW_TYPES: Record<TransactionLinkType, TransactionFlowType> = {
  [TransactionLinkType.LEASE]: TransactionFlowType.RENT,
  [TransactionLinkType.CONTACT]: TransactionFlowType.SUPPLIER,
  [TransactionLinkType.OWNER]: TransactionFlowType.OWNER,
};

const RESOLVED_STATUSES = [TransactionStatus.RECONCILED, TransactionStatus.PARTIALLY_RECONCILED];

export const OPEN_TRANSACTION_STATUSES = [
  TransactionStatus.TO_RECONCILE,
  TransactionStatus.PARTIALLY_RECONCILED,
  TransactionStatus.SNOOZED,
];

type MatchedBankMovement = Pick<
  Transaction,
  'amount' | 'links' | 'proposedLeaseId' | 'proposedContractorId' | 'proposedOwnerId'
>;

const getMatchedFlowType = (transaction: MatchedBankMovement): TransactionFlowType | undefined => {
  const linkType = transaction.links?.[0]?.linkType;
  if (!isNil(linkType)) {
    return LINK_TYPE_FLOW_TYPES[linkType];
  }
  const proposedTargets = {
    leaseIds: compact([transaction.proposedLeaseId]),
    supplierIds: compact([transaction.proposedContractorId]),
    ownerIds: compact([transaction.proposedOwnerId]),
  };
  if (hasLeaseAndCounterpartyRoles(proposedTargets)) {
    return getFlowTypeOfRoleDirection(transaction.amount, proposedTargets);
  }
  if (!isNil(transaction.proposedLeaseId)) {
    return TransactionFlowType.RENT;
  }
  if (!isNil(transaction.proposedContractorId)) {
    return TransactionFlowType.SUPPLIER;
  }
  if (!isNil(transaction.proposedOwnerId)) {
    return TransactionFlowType.OWNER;
  }
  return;
};

export const refineClassificationWithMatch = (
  classification: FlowClassification,
  transaction: MatchedBankMovement & Pick<Transaction, 'status'>
): FlowClassification => {
  const matchedFlowType = getMatchedFlowType(transaction);
  if (isNil(matchedFlowType) || classification.reason === TransactionClassificationReason.RETURN_CODE) {
    return classification;
  }
  const confidence = RESOLVED_STATUSES.includes(transaction.status)
    ? STRONG_CLASSIFICATION_CONFIDENCE
    : SUGGESTED_CLASSIFICATION_CONFIDENCE;
  if (confidence <= classification.confidence) {
    return classification;
  }
  return { flowType: matchedFlowType, confidence, reason: TransactionClassificationReason.MATCHED_TARGET };
};

export const getManualClassification = (linkType: TransactionLinkType): FlowClassification => ({
  flowType: LINK_TYPE_FLOW_TYPES[linkType],
  confidence: CERTAIN_CLASSIFICATION_CONFIDENCE,
  reason: TransactionClassificationReason.MANUAL,
});

const LINK_DERIVED_REASONS = [TransactionClassificationReason.MANUAL, TransactionClassificationReason.MATCHED_TARGET];

type ClassifiedBankMovement = Pick<
  Transaction,
  | 'amount'
  | 'bankTransactionCode'
  | 'proprietaryBankTransactionCode'
  | 'classification'
  | 'classificationConfidence'
  | 'classificationReason'
> & { bankAccount?: Pick<BankAccount, 'type'> | null };

const classifyFromBankCodes = (transaction: ClassifiedBankMovement): FlowClassification =>
  classifyTransactionFlow({
    amount: transaction.amount,
    bankTransactionCode: transaction.bankTransactionCode,
    proprietaryBankTransactionCode: transaction.proprietaryBankTransactionCode,
    ownAccountType: transaction.bankAccount?.type,
  });

const isTypeKeptAgainstEngineLink = ({
  classificationReason,
  classificationConfidence,
}: ClassifiedBankMovement): boolean =>
  classificationReason === TransactionClassificationReason.RETURN_CODE ||
  (!isNil(classificationReason) &&
    !LINK_DERIVED_REASONS.includes(classificationReason) &&
    (classificationConfidence ?? NO_CLASSIFICATION_CONFIDENCE) >= STRONG_CLASSIFICATION_CONFIDENCE);

export const getClassificationAfterLinkChange = (
  transaction: ClassifiedBankMovement,
  newLinks: Pick<TransactionLink, 'linkType'>[],
  reason: TransactionClassificationReason
): FlowClassification | undefined => {
  const firstLink = head(newLinks);
  if (!isNil(firstLink)) {
    if (reason === TransactionClassificationReason.MATCHED_TARGET && isTypeKeptAgainstEngineLink(transaction)) {
      return;
    }
    return {
      ...getManualClassification(firstLink.linkType),
      confidence:
        reason === TransactionClassificationReason.MANUAL
          ? CERTAIN_CLASSIFICATION_CONFIDENCE
          : STRONG_CLASSIFICATION_CONFIDENCE,
      reason,
    };
  }
  if (!isNil(transaction.classificationReason) && !LINK_DERIVED_REASONS.includes(transaction.classificationReason)) {
    return;
  }
  return classifyFromBankCodes(transaction);
};

export const getClassificationOfUntypedOpenMovement = (
  transaction: ClassifiedBankMovement &
    Pick<Transaction, 'proposedLeaseId' | 'proposedContractorId' | 'proposedOwnerId'>,
  links: TransactionLink[],
  status: TransactionStatus
): FlowClassification | undefined => {
  if (!isNil(transaction.classification) || !OPEN_TRANSACTION_STATUSES.includes(status)) {
    return;
  }
  return refineClassificationWithMatch(classifyFromBankCodes(transaction), { ...transaction, links, status });
};

export const readTransactionClassification = (
  transaction: Pick<Transaction, 'classification' | 'classificationConfidence' | 'classificationReason'>
): FlowClassification => ({
  flowType: transaction.classification ?? TransactionFlowType.UNKNOWN,
  confidence: transaction.classificationConfidence ?? NO_CLASSIFICATION_CONFIDENCE,
  reason: transaction.classificationReason ?? TransactionClassificationReason.NO_SIGNAL,
});

export const toTransactionClassificationFields = ({
  flowType,
  confidence,
  reason,
}: FlowClassification): Pick<Transaction, 'classification' | 'classificationConfidence' | 'classificationReason'> => ({
  classification: flowType,
  classificationConfidence: confidence,
  classificationReason: reason,
});

export enum ResolutionKind {
  LEASE = 'LEASE',
  SUPPLIER = 'SUPPLIER',
  OWNER = 'OWNER',
}

export const RESOLUTION_KIND_BY_LINK_TYPE: Record<TransactionLinkType, ResolutionKind> = {
  [TransactionLinkType.LEASE]: ResolutionKind.LEASE,
  [TransactionLinkType.CONTACT]: ResolutionKind.SUPPLIER,
  [TransactionLinkType.OWNER]: ResolutionKind.OWNER,
};

const SUGGESTED_RESOLUTION_RANK = 0;
const OTHER_RESOLUTION_RANK = 1;

const SUGGESTED_RESOLUTION_KINDS: Partial<Record<TransactionFlowType, ResolutionKind[]>> = {
  [TransactionFlowType.RENT]: [ResolutionKind.LEASE],
  [TransactionFlowType.DEPOSIT]: [ResolutionKind.LEASE],
  [TransactionFlowType.SUPPLIER]: [ResolutionKind.SUPPLIER],
  [TransactionFlowType.CALL_FOR_FUNDS]: [ResolutionKind.SUPPLIER],
  [TransactionFlowType.BANK_FEE]: [ResolutionKind.SUPPLIER],
  [TransactionFlowType.CREDIT]: [ResolutionKind.SUPPLIER],
  [TransactionFlowType.OWNER]: [ResolutionKind.OWNER],
};

export const orderResolutionsByFlowType = <T>(
  resolutions: T[],
  flowType: TransactionFlowType,
  getResolutionKind: (resolution: T) => ResolutionKind
): T[] => {
  const suggestedKinds = SUGGESTED_RESOLUTION_KINDS[flowType] ?? [];
  return sortBy(resolutions, (resolution) =>
    suggestedKinds.includes(getResolutionKind(resolution)) ? SUGGESTED_RESOLUTION_RANK : OTHER_RESOLUTION_RANK
  );
};
